import React, {
  useMemo,
  useCallback,
  useEffect,
  FunctionComponent,
} from 'react';
import { Popover, PopoverScreen, usePopover } from '@trello/nachos/popover';
import { Tooltip } from '@trello/nachos/tooltip';
import { PopoverList } from 'app/src/components/PopoverList';
import styles from './ListCell.less';
import { CellWrapper } from './CellWrapper';
import { Spinner } from '@trello/nachos/spinner';
import { useListHeaderCellQuery } from './ListHeaderCellQuery.generated';
import { useListCellMoveCardMutation } from './ListCellMoveCardMutation.generated';
import { forTemplate } from '@trello/i18n';
import { Analytics } from '@trello/atlassian-analytics';

const format = forTemplate('multi_board_table_view');

enum Screen {
  MoveListScreen,
}

interface ListCellProps {
  listName: string;
  idCard: string;
  idList: string;
  idBoard: string;
  idOrg: string;
  editable?: boolean;
}

interface ListChooserProps {
  idCard: string;
  idList: string;
  idBoard: string;
  idOrg: string;
  editable?: boolean;
}

export const ListCell: React.FunctionComponent<ListCellProps> = React.memo(
  ({ listName, idCard, idList, idBoard, idOrg, editable }) => {
    const { toggle, triggerRef, popoverProps } = usePopover<HTMLDivElement>({
      initialScreen: Screen.MoveListScreen,
    });

    useEffect(() => {
      if (popoverProps.isVisible) {
        Analytics.sendScreenEvent({
          name: 'changeListFromTableInlineDialog',
          containers: {
            organization: {
              id: idOrg,
            },
          },
        });
      }
    }, [popoverProps.isVisible, idOrg]);

    const toggleListPopover = useCallback(() => {
      Analytics.sendClickedButtonEvent({
        source: 'multiBoardTableViewScreen',
        buttonName: 'changeListButton',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });

      toggle();
    }, [toggle, idOrg]);

    return (
      <>
        <CellWrapper
          clickable={editable}
          onClick={toggleListPopover}
          ref={triggerRef}
        >
          <Tooltip content={listName} hideTooltipOnMouseDown tag="span">
            <div className={styles.listCell}>{listName}</div>
          </Tooltip>
        </CellWrapper>

        {editable && (
          <Popover title={format('change-list')} {...popoverProps}>
            <ListChooser
              idCard={idCard}
              idList={idList}
              idBoard={idBoard}
              idOrg={idOrg}
            />
          </Popover>
        )}
      </>
    );
  },
);

export const ListChooser: FunctionComponent<ListChooserProps> = ({
  idCard,
  idList,
  idBoard,
  idOrg,
}) => {
  const [changeCardList] = useListCellMoveCardMutation();

  const { popoverProps } = usePopover<HTMLDivElement>({
    initialScreen: Screen.MoveListScreen,
  });

  useEffect(() => {
    if (popoverProps.isVisible) {
      Analytics.sendScreenEvent({
        name: 'changeListFromTableInlineDialog',
        containers: {
          organization: {
            id: idOrg,
          },
        },
      });
    }
  }, [popoverProps.isVisible, idOrg]);

  const { data, loading } = useListHeaderCellQuery({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      idOrg,
      idBoards: [idBoard],
    },
  });

  const changeList = useCallback(
    async (id: string) => {
      if (id !== idList) {
        const traceId = Analytics.startTask({
          taskName: 'edit-card/idList',
          source: 'multiBoardTableViewScreen',
        });

        try {
          await changeCardList({
            variables: {
              cardId: idCard,
              listId: id,
              traceId,
            },
          });

          Analytics.sendTrackEvent({
            source: 'multiBoardTableViewScreen',
            action: 'moved',
            actionSubject: 'card',
            containers: {
              organization: {
                id: idOrg,
              },
              board: {
                id: idBoard,
              },
            },
          });

          Analytics.taskSucceeded({
            taskName: 'edit-card/idList',
            traceId,
            source: 'multiBoardTableViewScreen',
          });
        } catch (error) {
          throw Analytics.taskFailed({
            taskName: 'edit-card/idList',
            traceId,
            source: 'multiBoardTableViewScreen',
            error,
          });
        }
      }
    },
    [idCard, idList, idBoard, idOrg, changeCardList],
  );

  const items = useMemo(() => {
    const boards = data?.organization ? data.organization.boards : [];
    return boards.map((board) => ({
      name: board.name,
      items: board.lists.map(({ id, name }: { id: string; name: string }) => ({
        value: id,
        name,
        checked: id === idList,
        onClick: changeList,
      })),
    }));
  }, [data, idList, changeList]);

  if (loading) {
    return (
      <div>
        <Spinner centered />
      </div>
    );
  }

  return (
    <PopoverScreen id={Screen.MoveListScreen}>
      <PopoverList
        items={items}
        searchable
        searchPlaceholder={format('search-lists')}
      />
    </PopoverScreen>
  );
};
